import React, { useState, useEffect } from "react";
import { useAxiosInstance } from "../services/Interceptor";
import {
  GetEventCombinePrice,
  GetEventsByEventId,
} from "../services/EventsService";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setSubscriptionDetails,
  registerAccountStore,
} from "../store/slices/accountSlice";
import Nav from "../components/Nav";
import { PostPayment } from "../services/PaymentService";
import { registerAthleteWithEventId } from "../services/AthleteService";
import { useTrackEvent } from "../hooks/useTrackEvent";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

export default function AthleteMembershipPlans() {
  const [combinePrice, setCombinePrice] = useState(0);
  const [positionDrillPrice, setPositionDrillPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPositionDrills, setHasPositionDrills] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    combine: true,
    positionDrill: false,
  });

  const [eventNameSelected, setEventNameSelected] = useState();
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const navigate = useNavigate();

  const trackEvent = useTrackEvent();

  const storedData = JSON.parse(
    localStorage.getItem("registeredAccount") || "{}",
  );
  const accounts = useSelector((state) => state.account.register);
  const user = accounts.length ? accounts[0] : storedData;

  useEffect(() => {
    if (!accounts.length && storedData) {
      dispatch(registerAccountStore(storedData));
    }
  }, [dispatch, accounts.length, storedData]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, user.eventId);
        if (data) {
          setEventNameSelected(`${data.date} - ${data.name} - ${data.city}`);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadEvents();
  }, [apiInstance, user.eventId]);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        setIsLoading(true);

        const combineData = await GetEventCombinePrice(
          apiInstance,
          user.eventId,
        );
        if (combineData.success) {
          setCombinePrice(combineData?.data?.combinePrice ?? 0);
          setHasPositionDrills(!!combineData?.data?.hasPositionDrills);
          setPositionDrillPrice(combineData?.data?.positionDrillPrice ?? 0);
          trackEvent("Registration3_ViewedPricing", {
            event_id: user.eventId,
            combine_price: combineData?.data?.combinePrice ?? 0,
            position_drill_price: combineData?.data?.positionDrillPrice ?? 0,
          });
        }
      } catch (error) {
        console.error("Error fetching pricing:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
  }, [apiInstance, user.eventId, trackEvent]);

  useEffect(() => {
    const initial = {
      combine: true,
      positionDrill: false,
    };
    localStorage.setItem("subscriptionDetails", JSON.stringify(initial));
  }, []);

  const computeSelectedPrice = (opts) =>
    (combinePrice ?? 0) +
    (hasPositionDrills && opts.positionDrill ? (positionDrillPrice ?? 0) : 0);

  const handleSelectOption = (option) => {
    if (option === "positionDrill" && !hasPositionDrills) return;

    const updated = { ...selectedOptions, [option]: !selectedOptions[option] };
    updated.combine = true;

    setSelectedOptions(updated);

    const selectedPrice = computeSelectedPrice(updated);

    dispatch(
      setSubscriptionDetails({
        subscriptionType: option,
        subscriptionPrice: selectedPrice,
      }),
    );

    localStorage.setItem("subscriptionDetails", JSON.stringify(updated));
  };

  const handleContinueToPayment = async () => {
    const selectedPrice = computeSelectedPrice(selectedOptions);

    if (selectedPrice <= 0) {
      const userAthlete = await registerAthleteWithEventId(
        apiInstance,
        user,
        user.eventId,
      );

      try {
        const paymentPayloads = [];

        if (hasPositionDrills && selectedOptions.positionDrill) {
          paymentPayloads.push({
            userId: userAthlete.data.id,
            amount: 0,
            isCombine: false,
            isPositionDrill: true,
            isAnnualSubscription: false,
            isMonthlySubscription: false,
            isCash: false,
            eventId: user.eventId,
            transactionId: "N/A",
            intellipayToken: "",
          });
        }

        paymentPayloads.push({
          userId: userAthlete.data.id,
          amount: 0,
          isCombine: true,
          isPositionDrill: false,
          isAnnualSubscription: false,
          isMonthlySubscription: false,
          isCash: false,
          eventId: user.eventId,
          transactionId: "N/A",
          intellipayToken: "",
        });

        let allSuccessful = true;

        for (const payload of paymentPayloads) {
          const paid = await PostPayment(apiInstance, payload);
          if (paid.status !== 200) {
            allSuccessful = false;
            toast.error("Payment after spot registration failed.", {
              position: "top-right",
            });
            break;
          }
        }

        if (allSuccessful) {
          trackEvent("Registration5_RegistrationCompleted", {
            event_id: user.eventId,
            payment_method: "free",
          });
          toast.success("Athlete spot registration & payment successful!", {
            position: "top-right",
          });
          setTimeout(() => navigate("/register/registrationsuccess"), 3000);
        }
      } catch (err) {
        trackEvent("Registration4_5_PaymentFailed", {
          event_id: user.eventId,
          reason: err.message ?? "unknown",
        });
        console.error("Error during free registration:", err);
      }
    } else {
      window.location.href = "/register/subscription";
    }
  };

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-[#363636] text-white w-full max-w-3xl mx-auto pt-16">
        <h2 className="text-center mb-2 text-white uppercase text-3xl font-black italic">
          Combine Registration
        </h2>
        <p className="text-gray-300 text-center my-0">
          Choose the perfect plan to level up your performance
        </p>

        <div className="text-red-500 text-center px-4 text-xl my-4 mb-6">
          {eventNameSelected}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20 ">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-4xl text-red-500"
            />{" "}
          </div>
        ) : (
          <>
            <div className="bg-[#1B1B1B] p-4 rounded-lg flex justify-between items-center mb-4 space-x-6 mx-4">
              <div className="flex items-center space-x-4 w-full">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="h-5 w-5 text-red-500 border-gray-700 rounded"
                />
                <img className="h-10" alt="Trackit" src="/combine.svg" />
                <div className="w-full">
                  <div className="flex justify-between items-center border-b border-gray-600 pb-2">
                    <span className="text-2xl font-semibold text-left">
                      Single Combine Pass
                    </span>
                    <span className="text-2xl font-semibold float-right">
                      ${combinePrice.toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-400">
                      <span className="font-semibold text-gray-200 pr-2">
                        What's included:
                      </span>
                      • Complete Combine following NFL scouting combine protocol
                      <br />• Access to your verified combine numbers
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {hasPositionDrills && (
              <div className="bg-[#1B1B1B] p-4 rounded-lg flex justify-between items-center mb-4 space-x-6 mx-4">
                <div className="flex items-center space-x-4 w-full">
                  <input
                    type="checkbox"
                    checked={selectedOptions.positionDrill}
                    onChange={() => handleSelectOption("positionDrill")}
                    className="h-5 w-5 text-red-500 border-gray-700 rounded cursor-pointer"
                  />
                  <img className="h-10" alt="Trackit" src="/combine.svg" />
                  <div className="w-full">
                    <div className="flex justify-between items-center border-b border-gray-600 pb-2">
                      <span className="text-2xl font-semibold text-left">
                        Position Drills at this combine
                      </span>
                      <span className="text-2xl font-semibold float-right">
                        ${positionDrillPrice.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">&nbsp;</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 rounded-lg flex justify-between items-center max-w-3xl mx-auto">
              <div>
                <span className="text-sm font-normal text-red-500">
                  You selected:
                </span>
                <div className="flex flex-wrap gap-3 font-semibold">
                  <span>Single Combine Pass - ${combinePrice.toFixed(2)}</span>

                  {hasPositionDrills && selectedOptions.positionDrill && (
                    <span>
                      | Position Drills - ${positionDrillPrice.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-6 text-center">
                <button
                  className="bg-red-500 text-white uppercase rounded px-6 py-2 text-center hover:bg-red-700 cursor-pointer whitespace-nowrap"
                  onClick={handleContinueToPayment}
                >
                  PROCEED TO PAY
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
